import React, { useEffect, useRef, useCallback } from 'react';
import { FeatureFlags } from 'src/flags';
import { useFeatureFlags } from 'src/contexts/FeatureFlagsContext';

interface ScrollProviderProps {
  children: React.ReactNode;
  onScroll: (scrollPosition: number) => void;
  enabled: boolean;
  headerHeight: number;
  scrollThreshold?: number;
  isFirstLoad?: boolean;
}

const EditFormScrollProvider: React.FC<ScrollProviderProps> = ({
  children,
  onScroll,
  enabled,
  headerHeight,
  scrollThreshold = 50,
  isFirstLoad = false,
}) => {
  const hasInitialized = useRef(false);
  const isScrollBound = useRef(false);
  const enabledRef = useRef(enabled);

  // Update ref when enabled prop changes
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const { hasFeature } = useFeatureFlags();
  const handleScroll = useCallback(() => {
    // Check the latest enabled state via ref
    if (!enabledRef.current) {
      return;
    }

    const scrollPosition = window.scrollY;

    // Only force scroll to top on very first load
    if (isFirstLoad && !hasInitialized.current) {
      window.scrollTo(0, 0);
      hasInitialized.current = true;
      return;
    }

    onScroll(scrollPosition);
  }, [onScroll, isFirstLoad]);

  useEffect(() => {
    // Only attach scroll listener if enabled
    if (enabled && !isScrollBound.current) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      isScrollBound.current = true;
    }

    return () => {
      if (isScrollBound.current) {
        window.removeEventListener('scroll', handleScroll);
        isScrollBound.current = false;
      }
    };
  }, [handleScroll, enabled]);

  // Reset scroll position only on first load
  useEffect(() => {
    if (isFirstLoad && !hasInitialized.current) {
      window.scrollTo(0, 0);
      hasInitialized.current = true;
    }
  }, [isFirstLoad]);

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        flex: hasFeature(FeatureFlags.NGW_EDIT_SIDEPANEL_REDESIGN) ? 1 : '',
      }}
    >
      {children}
    </div>
  );
};

export default EditFormScrollProvider;
